import * as React from "react";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import Portal from "../Popups/Portal";
import classnames from "classnames";
import { all, config, IFilterConfig } from "../Map/config";
import { useNomaContext } from "../Contexts/MapContext";
import { useSideViewContext } from "../Contexts/SideViewContext";
import { useFiltersContext } from "../Contexts/FiltersContext";
import { IEsriFieldInfo } from "../Map/rest";
import { CloseButton } from "../Elements/CloseButton";
import { PopupButton } from "../Elements/PopupButton";
import { Checkbox } from "../Elements/Checkbox";
import "./SideView.scss";

export const SideView: FC = () => {
    const { sideViewVisible, setSideViewVisible } = useNomaContext();

    return (
        <div className="side-view-container shadow">
            <ShowSideViewButton></ShowSideViewButton>
            {sideViewVisible ? (
                <div className={classnames("sv-container unselectable", { "sv-hidden": !sideViewVisible })}>
                    <div className="sv-view">
                        <div className="sv-header">
                            <CloseButton onClose={() => setSideViewVisible(false)}></CloseButton>
                        </div>
                        <Content></Content>
                        <Footer></Footer>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

/********************************************************************************/
/********                 ShowSideViewButton                             ********/
/********************************************************************************/
const ShowSideViewButton = () => {
    const { setSideViewVisible } = useNomaContext();

    return (
        <Portal id={"side-view-show-button"}>
            <div
                className="sv-show-button"
                onClick={() => setSideViewVisible(true)}
                title="Sludinājumu meklēšanas kritēriji"
            >
                <span className="icon-zemesnoma-filter"></span>
            </div>
        </Portal>
    );
};

/********************************************************************************/
/********                 Content                                        ********/
/********************************************************************************/
const Content = () => (
    <div className="sv-content">
        <div className="sv-content-data">
            {config.sideView.filters.map((filter) => (
                <Filter filter={filter} key={"filter-" + filter.field}></Filter>
            ))}
            <TextFilter></TextFilter>
        </div>
    </div>
);

/********************************************************************************/
/********                 Footer                                         ********/
/********************************************************************************/
const Footer = () => {
    const { onClear, onFilter } = useSideViewContext();
    const { resultsCount } = useFiltersContext();
    const [countText, setCountText] = useState("");

    useEffect(() => {
        setCountText(
            !resultsCount
                ? "Nav atbilstošu objektu"
                : resultsCount === 1
                ? "Atlasīts 1 objekts"
                : "Atlasīti " + resultsCount + " objekti"
        );
    }, [resultsCount]);

    return (
        <div className="sv-footer">
            <div className="sv-row filter-buttons">
                <PopupButton title={"Notīrīt filtru"} classname={"light sv-button"} onClick={onClear}></PopupButton>
                <PopupButton title={"Meklēt"} classname={"dark sv-button"} onClick={onFilter}></PopupButton>
            </div>
            <div className="sv-row sv-count">{countText}</div>
        </div>
    );
};

/********************************************************************************/
/********                 Filter                                         ********/
/********************************************************************************/
const Filter = ({ filter }: { filter: IFilterConfig }) => {
    const { fieldInfos } = useNomaContext();
    const { error } = useSideViewContext();
    const hasError = error && error.fieldName === filter.field;

    const field = fieldInfos && fieldInfos["0"].find((f) => f.name === filter.field);

    return field ? (
        <div className={classnames("sv-row", { error: hasError })} key={filter.title + "-sv-row"}>
            <div className="sv-label">{filter.title}</div>
            <div className="sv-input">
                {field.domain && field.domain.codedValues ? (
                    config.sideView.filters.find((f) => f.field === field.name && f.multipleSelection) ? (
                        <MultiSelect field={field} />
                    ) : (
                        <Select field={field} />
                    )
                ) : (
                    <Range field={field}></Range>
                )}
            </div>
        </div>
    ) : null;
};

/********************************************************************************/
/********                 SelectNative                                   ********/
/********************************************************************************/
const SelectNative = ({ field }: { field: IEsriFieldInfo }) => {
    const { filters, setFilter, options, error } = useSideViewContext();
    const hasError = error && error.fieldName === field.name;

    return (
        <>
            <div className="select">
                <select
                    className="sv-select"
                    value={filters && filters[field.name] && filters[field.name].toString()}
                    onChange={(e) => setFilter(field.name, e.target.value)}
                >
                    {options &&
                        options[field.name] &&
                        options[field.name].map((cv) => (
                            <option key={cv.name + "-option-" + cv.code} value={cv.code}>
                                {cv.name}
                            </option>
                        ))}
                </select>
            </div>
            {hasError && <div className="error-message">{error.message}</div>}
        </>
    );
};
/********************************************************************************/
/********                 Select                                         ********/
/********************************************************************************/
const Select = ({ field }: { field: IEsriFieldInfo }) => {
    const { filters, setFilter, options, error } = useSideViewContext();
    const [expanded, setExpanded] = useState<boolean>(false);

    const selectRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        expanded && selectRef?.current?.focus();
    }, [expanded]);

    const hasError = error && error.fieldName === field.name;

    return (
        <>
            <div className="select">
                <div className="text-input" onClick={() => setExpanded(!expanded)}>
                    {filters && Object.keys(options).length
                        ? filters[field.name] && filters[field.name].toString() === all
                            ? all
                            : options[field.name].find((o) => {
                                  return o.code && o.code === filters[field.name];
                              })?.name
                        : null}
                </div>
            </div>
            {expanded ? (
                <div className="scrollable-content-container">
                    <div
                        className="single-select multi-options scrollable-content"
                        tabIndex={0}
                        ref={selectRef}
                        onBlur={(e) =>
                            //@ts-ignore
                            (!e.relatedTarget || e.relatedTarget?.type !== "checkbox") && setExpanded(false)
                        }
                    >
                        {options &&
                            options[field.name] &&
                            options[field.name].map((cv) => (
                                <div className="single-select-option" key={cv.name + "-option-" + cv.code}>
                                    <Checkbox
                                        label={cv.name}
                                        checked={filters[field.name] === cv.code}
                                        onChange={() => {
                                            setFilter(field.name, cv.code ? cv.code : all);
                                            setExpanded(false);
                                        }}
                                    ></Checkbox>
                                </div>
                            ))}
                    </div>
                </div>
            ) : null}
            {hasError && <div className="error-message">{error.message}</div>}
        </>
    );
};
/********************************************************************************/
/********                 MultiSelect                                    ********/
/********************************************************************************/
const MultiSelect = ({ field }: { field: IEsriFieldInfo }) => {
    const { filters, setFilter, options, error } = useSideViewContext();
    const [expanded, setExpanded] = useState<boolean>(false);

    const selectRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        expanded && selectRef?.current?.focus();
    }, [expanded]);

    const hasError = error && error.fieldName === field.name;

    const onChange = useCallback(
        (checked, cv) => {
            const filter = filters && filters[field.name];
            const newFilter = checked
                ? cv.name === all
                    ? all
                    : Array.isArray(filter) && !filter.includes(cv.code)
                    ? [...filter, cv.code]
                    : filter
                : cv.name === all
                ? []
                : filter === all
                ? options[field.name].map((o) => o.code && o.code !== cv.code && o.code).filter((c) => c)
                : (filter as Array<string | number>).filter((code) => code !== cv.code);

            setFilter(field.name, newFilter);
        },
        [filters, options]
    );

    return (
        <>
            <div className="multi-select">
                <div className="text-input" onClick={() => setExpanded(!expanded)}>
                    {filters && Object.keys(options).length
                        ? filters[field.name] && filters[field.name].toString() === all
                            ? all
                            : Array.isArray(filters[field.name]) && filters[field.name].length > 1
                            ? "izvēlētas " + filters[field.name].length + " vērtības"
                            : options[field.name].find((o) => {
                                  return o.code && o.code === filters[field.name][0];
                              })?.name
                        : null}
                </div>
            </div>
            {expanded ? (
                <div className="scrollable-content-container">
                    <div
                        className="multi-options scrollable-content"
                        tabIndex={0}
                        ref={selectRef}
                        onBlur={(e) => !e.relatedTarget && setExpanded(false)}
                    >
                        {options &&
                            options[field.name] &&
                            options[field.name].map((cv) => (
                                <div key={cv.name + "-option-" + cv.code}>
                                    <Checkbox
                                        label={cv.name}
                                        checked={
                                            (filters && filters[field.name] === all) ||
                                            (filters[field.name] as Array<string | number>).includes(cv.code)
                                        }
                                        onChange={(e) => onChange(e, cv)}
                                    ></Checkbox>
                                </div>
                            ))}
                    </div>
                </div>
            ) : null}
            {hasError && <div className="error-message">{error.message}</div>}
        </>
    );
};

/********************************************************************************/
/********                 Range                                          ********/
/********************************************************************************/
const Range = ({ field }: { field: IEsriFieldInfo }) => {
    const { filters, setRangeFilter } = useSideViewContext();
    const filter = filters && filters[field.name];

    return (
        <div className="sv-range">
            <input
                type="number"
                className="sv-num-input"
                placeholder="no"
                min={0}
                value={filter ? filter[0] : ""}
                onChange={(e) => setRangeFilter(field.name, Number(e.target.value))}
            ></input>
            <input
                type="number"
                className="sv-num-input"
                placeholder="līdz"
                min={0}
                value={filter ? filter[1] : ""}
                onChange={(e) => setRangeFilter(field.name, undefined, Number(e.target.value))}
            ></input>
        </div>
    );
};

/********************************************************************************/
/********                 TextFilter                                     ********/
/********************************************************************************/
const TextFilter = () => {
    const { filters, setFilter } = useSideViewContext();
    const filter = filters && filters[config.sideView.textFilter.field];

    return (
        <div className="sv-row text-row">
            <div className="sv-text">{config.sideView.textFilter.title}</div>
            <input
                className="sv-input text-input"
                value={filter ? filter.toString() : ""}
                onChange={(e) => setFilter(config.sideView.textFilter.field, e.target.value)}
            ></input>
        </div>
    );
};
